const CONSENT_COOKIE_NAME = 'cookie_consent'
const CONSENT_EXPIRY_DAYS = 365

export const cookieCategories = {
  necessary: {
    name: 'Necessary',
    description: 'Essential cookies for basic site functionality',
    required: true
  },
  analytics: {
    name: 'Analytics',
    description: 'Help us understand how visitors use our website',
    required: false
  },
  marketing: {
    name: 'Marketing',
    description: 'Used to track visitors across websites for advertising',
    required: false
  },
  preferences: {
    name: 'Preferences',
    description: 'Remember your preferences and settings',
    required: false
  }
}

class CookieService {
  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop().split(';').shift()
    }
    return null
  }

  setCookie(name, value, days = 365) {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    const expires = `expires=${date.toUTCString()}`
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`
  }

  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`
  }

  getConsentStatus() {
    const consent = localStorage.getItem(CONSENT_COOKIE_NAME)
    if (consent) {
      try {
        return JSON.parse(consent)
      } catch (e) {
        return null
      }
    }
    return null
  }

  saveConsentStatus(categories) {
    const consentData = {
      timestamp: new Date().toISOString(),
      categories: categories
    }
    localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(consentData))
    this.setCookie(CONSENT_COOKIE_NAME, 'granted', CONSENT_EXPIRY_DAYS)
  }

  clearConsent() {
    localStorage.removeItem(CONSENT_COOKIE_NAME)
    this.deleteCookie(CONSENT_COOKIE_NAME)
  }

  hasConsent() {
    return this.getConsentStatus() !== null
  }

  getCategoryConsent(category) {
    const consent = this.getConsentStatus()
    if (!consent) return false
    return consent.categories[category] === true
  }

  initializeAnalytics() {
    if (this.getCategoryConsent('analytics')) {
      console.log('Analytics cookies enabled')
      // Update Google consent if gtag is loaded
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        })
      }
    } else {
      // Revoke analytics consent
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        })
      }
      this.clearAnalyticsCookies()
    }
  }

  initializeMarketing() {
    if (this.getCategoryConsent('marketing')) {
      console.log('Marketing cookies enabled')
      // Update consent for advertising
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted'
        })
      }
    } else {
      // Revoke marketing consent
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied'
        })
      }
      this.clearMarketingCookies()
    }
  }

  clearAnalyticsCookies() {
    // Clear Google Analytics cookies
    const gaCookies = ['_ga', '_gid', '_gat', '_gac_']
    gaCookies.forEach(cookiePrefix => {
      document.cookie.split(';').forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim()
        if (cookieName.startsWith(cookiePrefix)) {
          this.deleteCookie(cookieName)
        }
      })
    })
  }

  clearMarketingCookies() {
    // Clear marketing/advertising cookies
    const marketingCookies = ['_fbp', 'fr', 'tr', '_gcl_', '_dc_gtm_']
    marketingCookies.forEach(cookiePrefix => {
      document.cookie.split(';').forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim()
        if (cookieName.startsWith(cookiePrefix)) {
          this.deleteCookie(cookieName)
        }
      })
    })
  }

  blockNonEssentialCookies() {
    // This function can be expanded to block specific third-party scripts
    // until consent is given
    const consent = this.getConsentStatus()
    if (!consent) {
      // Block analytics
      if (window.gtag) {
        window.gtag('consent', 'default', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied'
        })
      }
    }
  }
}

export default new CookieService()